import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Vessel } from 'anchor-shared/lib/model/entity/vessel';
import {
  getFadeInTransition,
  getFadeOutTransition,
} from '../../../../../../../../core/pure-functions/animations/ng-animations-provider';
import { ThemePalette } from '@angular/material/core';
import { VesselAvailabilityDialogService } from './dialogs/vessel-availability-dialog.service';
import { VesselAvailabilityPreferencesDAO } from '../../../../../../../../core/services/dao/vesselAvailabilityPreferencesDAO';
import { Subscription } from 'rxjs';
import { ProductOffering } from 'anchor-shared/lib/model/entity/productOffering';
import { ChickenTestDialogService } from '../../../../../../../shared/dialogs/chicken-test-dialog/chicken-test-dialog.service';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { ConstantMan } from '../../../../../../../../core/services/constantMan';
import { ProductOfferingDAO } from '../../../../../../../../core/services/dao/productOfferingDAO';
import { ContainsSubscriptions } from '../../../../../../../../core/services/interfaces/contains-subscriptions';
import { unsubscribeFromAll } from '../../../../../../../../core/pure-functions/rx-helper';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as moment from 'moment';
import { VesselAvailabilityDAO } from '../../../../../../../../core/services/dao/VesselAvailabilityDAO';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-boat-owner-availability',
  templateUrl: './boat-owner-availability.component.html',
  styleUrls: ['./boat-owner-availability.component.scss'],
  animations: [getFadeInTransition(1000), getFadeOutTransition(1000)],
})
export class BoatOwnerAvailabilityComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  editInline = false;
  showReservationSettings = false;
  showStartTimeDurations = false;
  isCheckedTemplated = false;
  checkedTemplate: string;
  checkedCustom: string;
  slideToggleArray = [null];
  optimalAvailability: boolean;
  colorCustom: ThemePalette;
  showCustomProducts = false;
  isMobile: boolean;
  customProducts: Partial<ProductOffering>[] = [];

  @Input()
  vessel: Vessel;
  subs: Subscription[] = [];
  existingProductToUpdate: Partial<ProductOffering>;
  inactiveProducts: Partial<ProductOffering>[] = [];
  activeProducts: Partial<ProductOffering>[] = [];
  allProductTimes: string[];
  showMoreProductTimes: boolean;
  canEditProducts: boolean = true;
  settingsType: string;

  constructor(
    private vesselAvailabilityDialogService: VesselAvailabilityDialogService,
    private chickenTestDialog: ChickenTestDialogService,
    private dialogService: VesselAvailabilityDialogService,
    private vesselAvailabilityPreferencesDAO: VesselAvailabilityPreferencesDAO,
    private breakpointObserver: BreakpointObserver,
    private productOfferingDAO: ProductOfferingDAO,
    private snackBar: MatSnackBar,
    private vesselAvailabilityDAO: VesselAvailabilityDAO,
  ) {
    const sub = this.breakpointObserver
      .observe(['(min-width: 960px)'])
      .subscribe((state: BreakpointState) => {
        this.isMobile = !state.matches;
      });
    this.subs.push(sub);
    this.customProducts.forEach((product) => {
      // @ts-ignore
      product.showMoreProductTimes = false;
    });
  }

  ngOnInit() {
    this.initVesselPreferences();
    this.initProductConflicts();
  }

  editClick(editType: string, settingsType?: string): void {
    if (editType === ConstantMan.VESSEL_PREFS.RESERVATION_SETTINGS) {
      this.editInline = true;
      if (this.isMobile) {
        const mobileDialog = this.vesselAvailabilityDialogService
          .openReservationSettingsDialog(this.vessel, settingsType)
          .subscribe((response) => {
            this.resetEditState();
          });
        this.subs.push(mobileDialog);
      } else {
        this.settingsType = settingsType;
        this.showReservationSettings = true;
      }
    } else if (editType === ConstantMan.VESSEL_PREFS.PRODUCT_FORM) {
      this.addOrUpdateNewProduct();
    } else {
      console.error('error with edit type');
    }
  }

  resetEditState(): void {
    this.editInline = false;
    this.showReservationSettings = false;
    this.showCustomProducts = false;
  }

  hoursToDays(hours: number) {
    switch (hours) {
      case 1:
        return '1 hour';
      case 24:
        return '1 day';
      case 72:
        return '3 days';
      case 168:
        return '7 days';
      default:
        return `${hours} hours`;
    }
  }

  numberOfMonthsToShow(Months: number) {
    switch (Months) {
      case 0:
        return 'Anytime';
      case 1:
        return '1 Month';
      default:
        return `${Months} Months`;
    }
  }

  allowsMultiple(): string {
    return this.vessel.vesselAvailabilityPreferences.allowsMultiple
      ? 'Yes'
      : 'No';
  }

  createDurationString(duration: number): string {
    if (duration === 4) {
      return 'Half Day (4 hours)';
    } else if (duration === 8) {
      return 'Full Day (8 hours)';
    }
    return '';
  }

  createDaysString(product: Partial<ProductOffering>): string {
    let daysArray = ['mon', 'tues', 'wed', 'thurs', 'fri', 'sat', 'sun'];
    let selectedDays = [];

    for (let day of daysArray) {
      if (product.days[day]) {
        selectedDays.push(day);
      }
    }

    return selectedDays.join(', ');
  }

  createProductTimesString(
    product: Partial<ProductOffering>,
    showMoreString?: boolean,
  ): string {
    if (product.openAvailability) {
      return `${product.openAvailabilityStartTime} - ${product.openAvailabilityEndTime}`;
    } else {
      let startTimes = product.customAvailabilityTimes.map((time) => {
        return time.startTime;
      });

      if (product.flexTimes) {
        let shortTimeString = [];
        let flexTimes = this.getFlexTimes(startTimes);
        this.allProductTimes = [...startTimes, ...flexTimes];
        this.allProductTimes = this.sortTimes(this.allProductTimes);

        if (showMoreString) {
          this.allProductTimes.forEach((time) => {
            if (this.allProductTimes.indexOf(time) <= 2) {
              shortTimeString.push(time);
            }
          });
          return shortTimeString.join(', ');
        } else {
          return this.allProductTimes.join(', ');
        }
      } else {
        return startTimes.join(', ');
      }
    }
  }

  toggleProductStatus(product: Partial<ProductOffering>): void {
    if (this.canEditProducts) {
      this.canEditProducts = false;
      product.active = !product.active;
      const updateProductSub = this.productOfferingDAO
        .update(this.vessel.id, product.id, product)
        .subscribe((updatedProduct) => {
          this.initProductConflicts();
          this.createVesselAvailability();
        });
      this.subs.push(updateProductSub);
    }
  }

  createVesselAvailability(): void {
    this.vesselAvailabilityDAO
      .updateFullAvailabilitiesForVessel(this.vessel.id)
      .pipe(take(1))
      .subscribe(() => {
        this.canEditProducts = true;
      });
  }

  deleteProduct(product: Partial<ProductOffering>): void {
    if (this.canEditProducts) {
      this.canEditProducts = false;
      const chickenSub = this.chickenTestDialog
        .open(
          `Are you sure you want to delete your product: ${product.productName}?`,
          'Yes, delete',
          'Cancel',
          80,
        )
        .subscribe((result) => {
          if (result) {
            const deleteProductSub = this.productOfferingDAO
              .delete(this.vessel.id, product.id)
              .subscribe((deleted) => {
                if (deleted) {
                  this.customProducts = this.customProducts.filter(
                    (customProduct) => customProduct.id !== product.id,
                  );
                  this.createVesselAvailability();
                }
              });

            this.subs.push(deleteProductSub);
          } else {
            this.canEditProducts = true;
          }
        });
      this.subs.push(chickenSub);
    }
  }

  updateProductOfferings($event: Partial<ProductOffering>) {
    const conflictSub = this.productOfferingDAO
      .activeProductConflicts(
        this.vessel.id,
        this.vessel.vesselAvailabilityPreferences.id,
        $event,
      )
      .subscribe((conflicts) => {
        if (conflicts) {
          // @ts-ignore
          $event.conflicts = conflicts;
          $event.active = false;
        } else {
          // @ts-ignore
          $event.conflicts = [];
        }
        this.initProductConflicts();
      });
    this.subs.push(conflictSub);

    this.customProducts = this.customProducts.filter(
      (product) => product.id !== $event.id,
    );
    this.customProducts.push($event);
  }

  addOrUpdateNewProduct(product: Partial<ProductOffering> = null): void {
    this.existingProductToUpdate = product;
    this.editInline = true;
    if (this.isMobile) {
      const openProductOfferingDialog = this.vesselAvailabilityDialogService
        .openProductOfferingDialog(this.vessel, product)
        .subscribe((response: any) => {
          this.resetEditState();
          if (response && response.product) {
            const conflictSub = this.productOfferingDAO
              .activeProductConflicts(
                this.vessel.id,
                this.vessel.vesselAvailabilityPreferences.id,
                response.product,
              )
              .subscribe((conflicts) => {
                if (conflicts) {
                  response.product.conflicts = conflicts;
                  response.product.active = false;
                } else {
                  response.product.conflicts = [];
                }
                this.updateProductOfferings(response.product);
                this.initProductConflicts();
              });
            this.subs.push(conflictSub);
          }
        });
      this.subs.push(openProductOfferingDialog);
    } else {
      this.showCustomProducts = true;
    }
  }

  openSnackbar(message: string) {
    this.snackBar.open(message, null, {
      duration: 5000,
      verticalPosition: 'top',
    });
  }

  updateAvailabilitySelections(): void {
    let open, optimal, custom;
    let snackText = '';
    if (this.isCheckedTemplated) {
      optimal = this.optimalAvailability;
      open = !this.optimalAvailability;
      custom = false;
      if (optimal) {
        snackText = 'Saved. Set to Optimal Availability.';
      } else {
        snackText = 'Saved. Set to Open Availability.';
      }
    } else {
      snackText = 'Saved. Set to Custom Products.';
      custom = true;
      optimal = false;
      open = false;
    }

    const updateSub = this.vesselAvailabilityPreferencesDAO
      .update(this.vessel.id, this.vessel.vesselAvailabilityPreferences.id, {
        openAvailability: open,
        optimalAvailability: optimal,
        customAvailability: custom,
      })
      .subscribe((updatedSuccess) => {
        if (updatedSuccess) {
          this.openSnackbar(snackText);
          this.setAvailabilitySelectionState(open, optimal, custom);
        }
      });
    this.subs.push(updateSub);
  }

  initProductConflicts() {
    const productSub = this.productOfferingDAO
      .findByVesselPreferencesId(
        this.vessel.id,
        this.vessel.vesselAvailabilityPreferences.id,
      )
      .subscribe((products) => {
        this.customProducts = products;
        this.customProducts.forEach((product) => {
          if (product.active) {
            this.activeProducts.push(product);
          } else {
            this.inactiveProducts.push(product);
          }
        });

        this.findInactiveProductConflicts();
      });
    this.subs.push(productSub);
  }

  ngOnDestroy(): void {
    unsubscribeFromAll(this.subs);
  }

  private setAvailabilitySelectionState(
    open: boolean,
    optimal: boolean,
    custom: boolean,
  ): void {
    this.vessel.vesselAvailabilityPreferences.openAvailability = open;
    this.vessel.vesselAvailabilityPreferences.optimalAvailability = optimal;
    this.vessel.vesselAvailabilityPreferences.customAvailability = custom;
  }

  private initVesselPreferences() {
    if (this.vessel.vesselAvailabilityPreferences) {
      const productSub = this.productOfferingDAO
        .findByVesselPreferencesId(
          this.vessel.id,
          this.vessel.vesselAvailabilityPreferences.id,
        )
        .subscribe((products) => {
          this.customProducts = products;
        });
      this.subs.push(productSub);

      this.customProducts =
        this.vessel.vesselAvailabilityPreferences.productOfferings;

      if (
        this.vessel.vesselAvailabilityPreferences.openAvailability ||
        this.vessel.vesselAvailabilityPreferences.optimalAvailability
      ) {
        this.isCheckedTemplated = true;
        this.optimalAvailability =
          !this.vessel.vesselAvailabilityPreferences.openAvailability;
      } else {
        this.isCheckedTemplated = false;
      }
    }
  }

  private findInactiveProductConflicts() {
    this.inactiveProducts.forEach((product) => {
      const productConflictSub = this.productOfferingDAO
        .activeProductConflicts(
          this.vessel.id,
          this.vessel.vesselAvailabilityPreferences.id,
          product,
        )
        .subscribe((conflict) => {
          // @ts-ignore
          product.conflicts = [];
          if (conflict) {
            // @ts-ignore
            product.conflicts = conflict;
          }
        });
      this.subs.push(productConflictSub);
    });
  }

  private getFlexTimes(selectableTimes: string[]) {
    let earliestStartTime = moment('11:59 PM', 'h:mm A');
    let latestStartTime = moment('12:01 AM', 'h:mm A');

    selectableTimes.forEach((time) => {
      let tmpStartTime = moment(time, 'h:mm A');
      if (tmpStartTime.isAfter(latestStartTime)) {
        latestStartTime = tmpStartTime;
      }
      if (tmpStartTime.isBefore(earliestStartTime)) {
        earliestStartTime = tmpStartTime;
      }
    });
    let startIndex = availableTimes.indexOf(
      earliestStartTime.format('h:mm A').toString(),
    );
    let endIndex = availableTimes.indexOf(
      latestStartTime.format('h:mm A').toString(),
    );

    let flexTimes = [
      availableTimes[startIndex - 1],
      availableTimes[startIndex - 2],
      availableTimes[startIndex - 3],
      availableTimes[startIndex - 4],
      availableTimes[endIndex + 1],
      availableTimes[endIndex + 2],
      availableTimes[endIndex + 3],
      availableTimes[endIndex + 4],
    ];

    let filteredFlexTimes = flexTimes.filter((x): x is string => !!x);
    return filteredFlexTimes;
  }

  sortTimes(timeArray: string[]): string[] {
    return timeArray.sort((a, b) => {
      return moment(a, 'h:mm A').unix() - moment(b, 'h:mm A').unix();
    });
  }

  getProductTimesLength(listedTimes: string[]) {
    let length = listedTimes.length;
    return length - 3;
  }
}

export const availableTimes = [
  '5:00 AM',
  '5:30 AM',
  '6:00 AM',
  '6:30 AM',
  '7:00 AM',
  '7:30 AM',
  '8:00 AM',
  '8:30 AM',
  '9:00 AM',
  '9:30 AM',
  '10:00 AM',
  '10:30 AM',
  '11:00 AM',
  '11:30 AM',
  '12:00 PM',
  '12:30 PM',
  '1:00 PM',
  '1:30 PM',
  '2:00 PM',
  '2:30 PM',
  '3:00 PM',
  '3:30 PM',
  '4:00 PM',
  '4:30 PM',
  '5:00 PM',
  '5:30 PM',
  '6:00 PM',
  '6:30 PM',
  '7:00 PM',
  '7:30 PM',
  '8:00 PM',
  '8:30 PM',
  '9:00 PM',
  '9:30 PM',
  '10:00 PM',
  '10:30 PM',
  '11:00 PM',
  '11:30 PM',
];
